<template>
  <v-form ref="form" class="form">
    <v-row>
      <v-col class="py-0" cols="12">
        <v-text-field
          v-model="visualization.name"
          :label="$t('visualizations.name')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.requiredWithEmptyString, backEndValidation('name')]
          "
          :readonly="readonly"
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-combobox
          :items="symbols.map(i => i.symbol)"
          v-model="visualization.symbol"
          @change="() => setSymbolName('symbol_name', visualization.symbol)"
          :label="$t('visualizations.symbol')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [rules.requiredWithEmptyString, backEndValidation('symbol')]
          "
          :readonly="readonly"
        ></v-combobox>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-text-field
          v-model="visualization.symbol_name"
          :label="$t('visualizations.symbol-name')"
          :loading="resourceLoading"
          :rules="
            resourceLoading
              ? []
              : [
                  rules.requiredWithEmptyString,
                  backEndValidation('symbol_name')
                ]
          "
        ></v-text-field>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <v-text-field
          v-model="visualization.display_factor"
          :label="$t('visualizations.display-factor')"
          :loading="resourceLoading"
          :rules="resourceLoading ? [] : [backEndValidation('display_factor')]"
          :readonly="readonly"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-divider class="my-5" />
    <h2>{{ $t("visualizations.chart-options") }}</h2>
    <v-row>
      <v-col class="py-0" cols="12" sm="12">
        <v-autocomplete
          v-model="visualization.chart_type"
          :items="chart_types"
          :label="$t('visualizations.chart-type')"
          :loading="resourceLoading"
          :item-text="i => $t(`visualizations.chart-types.${i.name}`)"
          item-value="name"
          :rules="
            resourceLoading
              ? []
              : [rules.requiredWithEmptyString, backEndValidation('chart_type')]
          "
          :readonly="readonly"
        ></v-autocomplete>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <p>{{ $t("visualizations.chart-series-color") }}</p>
        <v-color-picker
          ref="chart-series"
          v-model="chart_series_color"
          mode="hexa"
          v-on:update:color="setFillColor"
          :disabled="resourceLoading || readonly"
        ></v-color-picker>
      </v-col>
      <v-col class="py-0" cols="12" sm="6">
        <p>{{ $t("visualizations.chart-series-fill-color") }}</p>
        <v-color-picker
          v-model="chart_series_fill_color"
          mode="hexa"
          :disabled="resourceLoading || readonly"
        ></v-color-picker>
      </v-col>
    </v-row>
    <br />
    <v-btn
      color="primary mr-3"
      v-on:click="save()"
      :loading="loading"
      :disabled="readonly"
    >
      <v-icon left>{{ $props.button_icon }}</v-icon>
      {{ $t(`visualizations.${$props.button_title}`) }}
    </v-btn>
  </v-form>
</template>

<script>
import { rules } from "@/components/mixins/rules";
import { projectPermissions } from "@/components/mixins/projects/projectPermissions";

export default {
  name: "FormVisualization",
  mixins: [rules, projectPermissions],
  data: () => ({
    loading: false,
    chart_series_color: { r: 255, g: 200, b: 0, a: 1 },
    chart_series_fill_color: { r: 255, g: 200, b: 0, a: 0.1 },
    chart_type: null,
    chart_types: [
      { name: "line" },
      { name: "spline" },
      { name: "area" },
      { name: "areaspline" },
      { name: "column" }
    ]
  }),
  props: {
    dispatch: String,
    button_title: String,
    button_icon: String,
    visualization: {
      default: function() {
        return {
          chart_type: "",
          symbol_name: "",
          symbol: "",
          name: ""
        };
      }
    },
    resourceLoading: {
      type: Boolean,
      default: false
    },
    permission: {
      type: String,
      default: ""
    }
  },
  computed: {
    symbols() {
      return this.$store.getters["presetStore/getSymbolPreset"];
    },
    project_id() {
      return this.$route.params.project_id;
    },
    project() {
      return this.$store.getters["projectStore/getCurrentItem"];
    },
    readonly: {
      get() {
        return !this.hasCurrentProjectPermissions(this.permission);
      }
    }
  },
  methods: {
    setRGBAFromMeter(visualization) {
      visualization = visualization || this.visualization;
      function formRGBA(color) {
        let matcher = /rgba\(([\d]+),([\d]+),([\d]+),([\d]+|[\d]*.[\d]+)\)/.exec(
          color
        );
        if (matcher) {
          return { r: matcher[1], g: matcher[2], b: matcher[3], a: matcher[4] };
        }
        return false;
      }
      this.$set(
        this,
        "chart_series_color",
        formRGBA(visualization.chart_series_color) || this.chart_series_color
      );
      this.$set(
        this,
        "chart_series_fill_color",
        formRGBA(visualization.chart_series_fill_color) ||
          this.chart_series_fill_color
      );
    },
    formRGBAString: obj => `rgba(${obj.r},${obj.g},${obj.b},${obj.a})`,
    save(callback = this.defaultCallbackForSave) {
      this.clearBackEndErrors();
      if (this.$refs.form.validate()) {
        this.loading = true;
        let item = {
          ...this.visualization,
          project_id: this.project_id,
          id: this.$route.params.id,
          chart_series_color: this.formRGBAString(this.chart_series_color),
          chart_series_fill_color: this.formRGBAString(
            this.chart_series_fill_color
          )
        };
        this.$store
          .dispatch(this.$props.dispatch, {
            item,
            errorHandler: this.errorHandler.bind(this, () => {
              this.$refs.form.validate();
            }),
            onSuccess: item => {
              if (item) {
                callback(item);
              }
            }
          })
          .finally(() => (this.loading = false));
      }
    },
    defaultCallbackForSave(item) {
      this.$store.commit("projectMeteringStore/visualizationStore/set", {
        item
      });
      this.$router.push({
        name: "visualization",
        params: {
          id: item.id
        }
      });
    },
    setSymbolName(parameterName, symbol) {
      let currentSymbol = this.symbols.find(i => i.symbol === symbol);
      if (currentSymbol) {
        this.visualization[parameterName] = this.$t(currentSymbol.name);
      }
    },
    setFillColor(i) {
      let newRgba = this.formRGBAString(i.rgba);
      if (newRgba === this.visualization.chart_series_color) {
        return;
      }
      this.$set(this, "chart_series_fill_color", { ...i.rgba, a: 0.1 });
    }
  },
  watch: {
    visualization: {
      handler: function(visualization) {
        this.setRGBAFromMeter(visualization);
      },
      deep: true
    },
    "visualization.chart_type": function(newVal) {
      if (!newVal) {
        this.visualization.chart_type = null;
      }
    },
    "visualization.symbol_name": function(newVal) {
      if (!newVal) {
        this.visualization.symbol_name = null;
      }
    },
    "visualization.symbol": function(newVal) {
      if (!newVal) {
        this.visualization.symbol = null;
      }
    },
    "visualization.name": function(newVal) {
      if (!newVal) {
        this.visualization.name = null;
      }
    }
  },
  mounted() {
    this.$store.dispatch("projectMeteringStore/visualizationStore/getList", {
      params: {
        limit: -1,
        project_id: this.project_id
      },
      onSuccess: () => {
        this.loading = false;
      }
    });
    this.setRGBAFromMeter();
  }
};
</script>
